import moment from 'moment-timezone';

import { formatTokenWait, secondsUntilTokenReset } from '@mummssoftware/utils';
import type {
  PatientConversationSummary,
  TokenStatus,
} from '@mummssoftware/utils';

import type { ConversationQuestion, MaiaDateRange } from '../types';
import defaults from '../translations/en.json';

const SHORT_DATE = 'MM-DD-YY';
const DATE_TIME = 'MM-DD-YY HH:mm';
const TIME = 'HH:mm';
const EN_DASH = '–';
const EMPTY = '—';

/**
 * Gateway timestamps are LocalDateTime strings with no zone, written by a
 * server running in UTC. Read them as UTC and show them in the site's zone
 * (same convention as chart-audit), falling back to the browser's zone.
 */
const gatewayMoment = (value: string, siteTimezone?: string | null) => {
  // ISO-only parse: anything else is invalid rather than moment's Date() fallback
  const utc = moment.utc(value, moment.ISO_8601);
  return siteTimezone ? utc.tz(siteTimezone) : utc.local();
};

export const formatShortDate = (date: Date): string =>
  moment(date).format(SHORT_DATE);

/** 'MM-DD-YY HH:mm' for a gateway timestamp; '' when missing or unparseable. */
export const formatDateTime = (
  value: string | null | undefined,
  siteTimezone?: string | null,
): string => {
  if (!value) {
    return '';
  }
  const parsed = gatewayMoment(value, siteTimezone);
  return parsed.isValid() ? parsed.format(DATE_TIME) : '';
};

/** 'HH:mm' for a gateway timestamp (a question under its dated conversation); '' when missing or unparseable. */
export const formatTime = (
  value: string | null | undefined,
  siteTimezone?: string | null,
): string => {
  if (!value) {
    return '';
  }
  const parsed = gatewayMoment(value, siteTimezone);
  return parsed.isValid() ? parsed.format(TIME) : '';
};

/**
 * 'YYYY-MM-DD' of a gateway timestamp in the site's zone, so a date filter
 * matches the day the user sees in the cell; '' when missing or unparseable.
 */
export const gatewayDay = (
  value: string | null | undefined,
  siteTimezone?: string | null,
): string => {
  if (!value) {
    return '';
  }
  const parsed = gatewayMoment(value, siteTimezone);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

/** '04-01-26 – 10-01-26' (en dash), as in the header date button. */
export const formatRangeLabel = (range: MaiaDateRange): string =>
  `${formatShortDate(range.fromDate)} ${EN_DASH} ${formatShortDate(
    range.toDate,
  )}`;

/** Whole seconds from one gateway timestamp to a later one; null when either is missing. */
export const secondsBetween = (
  from: string | null | undefined,
  to: string | null | undefined,
): number | null => {
  if (!from || !to) {
    return null;
  }
  const start = moment.utc(from, moment.ISO_8601);
  const finish = moment.utc(to, moment.ISO_8601);
  if (!start.isValid() || !finish.isValid()) {
    return null;
  }
  return Math.max(0, finish.diff(start, 'seconds'));
};

/** A question on one line (the grid ellipsizes it). */
export const oneLine = (text: string | null | undefined) =>
  (text || '').replace(/\s+/g, ' ').trim();

/** Topic column: the agent's title for the conversation, on one line. */
export const conversationTopic = (
  row: Pick<PatientConversationSummary, 'topic'>,
) => oneLine(row.topic);

/** Model column: the model family only, e.g. 'gemini-3.8-flash' -> 'gemini'. */
export const modelFamily = (model: string | null | undefined) =>
  (model || '').split('-')[0];

const pad = (n: number) => String(n).padStart(2, '0');

/** 'mm:ss' under an hour, 'h:mm:ss' from an hour up; '—' for null. */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds)) {
    return EMPTY;
  }
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${pad(minutes)}:${pad(secs)}`;
};

/** '3,412'; '—' for null. */
export const formatTokens = (tokens: number | null | undefined): string =>
  tokens == null ? EMPTY : tokens.toLocaleString('en-US');

/** '$1.23', '<$0.01' under a cent, '$0.00' for nothing spent; '—' for null. */
export const formatCost = (usd: number | null | undefined): string => {
  if (usd == null || Number.isNaN(usd)) {
    return EMPTY;
  }
  if (usd > 0 && usd < 0.01) {
    return '<$0.01';
  }
  return `$${usd.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** '$0.0123' (hover text: the cents a small cost rounds away); '' for null. */
export const formatCostExact = (usd: number | null | undefined): string =>
  usd == null || Number.isNaN(usd) ? '' : `$${usd.toFixed(4)}`;

const UNANSWERED_LABELS: Record<string, string> = {
  cancelled: defaults.unansweredCancelled,
  timedOut: defaults.unansweredTimedOut,
  chartNotReady: defaults.unansweredChartNotReady,
  chartFailed: defaults.unansweredChartFailed,
};

/** Why a question got no answer, in words; 'No answer' for an error or anything new. */
export const unansweredLabel = (outcome: string): string =>
  UNANSWERED_LABELS[outcome] || defaults.unansweredError;

/** Display name snapshotted at start, else the login id. */
export const personLabel = (
  row: Pick<PatientConversationSummary, 'userDisplayName' | 'userId'>,
): string => row.userDisplayName || row.userId || '';

/** Replaces each {name} in a translation with its value. */
export const fillTemplate = (
  template: string,
  values: Record<string, string>,
): string =>
  Object.entries(values).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(value),
    template,
  );

/** '3,412 input tokens, 2,900 read from the cache' (hover text). */
export const inputTokensTitle = (
  template: string,
  input: number | null | undefined,
  cached: number | null | undefined,
): string =>
  fillTemplate(template, {
    input: formatTokens(input),
    cached: formatTokens(cached),
  });

/** '520 output tokens: 400 answer, 120 thinking' (hover text). */
export const outputTokensTitle = (
  template: string,
  output: number | null | undefined,
  candidates: number | null | undefined,
  thoughts: number | null | undefined,
): string =>
  fillTemplate(template, {
    output: formatTokens(output),
    candidates: formatTokens(candidates),
    thoughts: formatTokens(thoughts),
  });

/** '09-28-26 14:12 · A. Nguyen (RN) · 00:14 · 3,412 tokens' — the past conversation's summary chip. */
export const pastSummaryLabel = (
  row: PatientConversationSummary,
  tokensWord: string,
  siteTimezone?: string | null,
): string =>
  [
    formatDateTime(row.startedAt, siteTimezone),
    personLabel(row),
    formatDuration(row.durationSeconds),
    `${formatTokens(row.totalTokensUsed)} ${tokensWord}`,
  ]
    .filter(Boolean)
    .join(' · ');

/** '10-08-26 09:09 · A. Nguyen (RN) · 00:04 · 19,438 tokens' — one past question's summary chip. */
export const questionSummaryLabel = (
  conversation: PatientConversationSummary,
  row: ConversationQuestion,
  tokensWord: string,
  siteTimezone?: string | null,
): string =>
  [
    formatDateTime(row.startsAt, siteTimezone),
    personLabel(conversation),
    formatDuration(row.durationSeconds),
    `${formatTokens(row.details.tokensUsed)} ${tokensWord}`,
  ]
    .filter(Boolean)
    .join(' · ');

/** The ticket's limit message with the wait until the pool resets. */
export const tokenLimitText = (
  status: TokenStatus,
  now: number = Date.now(),
): string =>
  defaults.tokenLimitReached.replace(
    '{wait}',
    formatTokenWait(secondsUntilTokenReset(status, now)),
  );
